import { ChatCompletionRequestMessage } from "openai";
import { getRectOfNodes, Node, ReactFlowInstance, Rect, XYPosition } from "reactflow";
import { getChatGPTRelatedTopics } from "../../api/openai-api";
import { projectTitle, uuid } from "../utils";
import { BrainstormNodeData } from "./brainstorm-node/brainstorm-node.model";
import { ChatNodeData } from "./response-node/response-node.model";
import { MemoNodeData } from "./memo-node/memo-node.model";
import { createTopicNodeToReturn } from "./topic-node/topic-node.helper";
import { TopicNodeData } from "./topic-node/topic-node.model";

export const duplicateNode = (node: Node) => {
  const copy: Node = JSON.parse(JSON.stringify(node));
  copy.id = `${node.type}-${uuid()}`;
  copy.parentNode = '';
  return copy;
}

export const extractNodeTexts = (nodes: Node[]): string[] => {
  return nodes.map(node => {
    if (node.type === 'chat') {
      if ((node.data as ChatNodeData).state.response) {
        return `${(node.data as ChatNodeData).state.input}: 
          ${(node.data as ChatNodeData).state.response}`;
      } else {
        return node.data.state.input ?? '';
      }
    } else if (node.type === 'topic') {
      return (node.data as TopicNodeData).state.topic ?? '';
    } else if (node.type === 'memo') {
      return (node.data as MemoNodeData).state.memo ?? '';
    } else if (node.type === 'brainstorm') {
      return `${(node.data as BrainstormNodeData).state.input}: 
        ${Object.values((node.data as BrainstormNodeData).state.questions ?? {}).flat().join(', ')}`;
    } else {
      return '';
    }
  }).filter((text: string) => !!text && !text.includes(projectTitle));
}

export const getCenterOfNodes = (nodes: Node[]): XYPosition => {
  const rect = getRectOfNodes(nodes);
  const leftMostNode = nodes.find(node => node.position.x <= rect.x);
  return {
    x: rect.x + rect.width/2 - (leftMostNode?.width ?? 0)/2,
    y: rect.y + rect.height/2
  };
}

/**
 * Given context and origin nodes, generate and add recommended subtopics from
 * the origin nodes
 */
export const predictSubtopics = async (
  context: string[],
  originNodeIds: string[],
  reactFlowInstance: ReactFlowInstance,
  position?: string, // 'around', 'right-side'
) => {
  return getChatGPTRelatedTopics(context.join(''), 6)
    .then(topics => {
      const originNodes = originNodeIds
        .map(nodeId => reactFlowInstance.getNode(nodeId))
        .filter((node): node is Node => !!node);
      let surroundPositions: XYPosition[] = [];
      if (position === 'right-side') {
        surroundPositions = calculateRightSidePositions(topics.length, getCenterOfNodes(originNodes), getRectOfNodes(originNodes));
      } else {
        // Default is always 'around'
        surroundPositions = calculateSurroundPositions(topics.length, getCenterOfNodes(originNodes), getRectOfNodes(originNodes));
      }
      const chatHistory: ChatCompletionRequestMessage[] = [{
        role: 'user',
        content: `The topic of the conversation is focused on "${extractNodeTexts(originNodes).join(', ')}"`,
      }];
      topics.filter((topic): topic is string => !!topic).forEach((topic, i) => {
        setTimeout(() => {
          reactFlowInstance.addNodes(createTopicNodeToReturn(
            topic,
            '', // No parent
            surroundPositions[i],
            chatHistory,
            true
          ));
        }, 400*i);
      });
    });
}

export const calculateSurroundPositions = (
  n: number,
  {x, y}: XYPosition,
  rect: Rect,
) => {
  let positions: XYPosition[] = [];
  const xRadius = Math.min(Math.max(150, 1.7*rect.width), 400);
  const yRadius = Math.min(Math.max(150, 1.7*rect.height), 400);

  for (let i = 0; i < n; i += 1) {
    const theta = ((Math.PI*2)*i)/n;
    let dx = Math.cos(theta);
    let dy = Math.sin(theta);
    positions.push({x: x+(xRadius*dx), y: y+(yRadius*dy)-rect.height/2});
  }
  return positions;
}

export const calculateRightSidePositions = (
  n: number,
  {x, y}: XYPosition,
  rect: Rect,
) => {
  let positions: XYPosition[] = [];
  for (let i = 0; i < n; i += 1) {
    let dx = rect.width/2 + 200;
    let dy = i*(Math.max(rect.height, 400))/n;
    positions.push({x: x+dx, y: y+dy-rect.height/2});
  }
  return positions;
}

// Delete any recommended nodes that were ignored
export const deleteRecommendedNodes = (
  nodes: Node[]
) => {
  return nodes.filter(node => {
    if (node.type === 'topic') {
      return !(node.data as TopicNodeData).state.isRecommended;
    } else {
      return true;
    }
  });
}
